/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { useWorkspacePresence } from "../hooks/useWorkspacePresence";
import { isDemoMode } from "../lib/supabase";
import { type DisplayPresenceStatus, type PresenceStatus } from "../types/presence";
import type { ManualPresenceStatus } from '../utils/presenceStatus';
import { createShowcaseUsers } from '../demo/showcaseCatalog';

interface PresenceEntry { 
  id: string; 
  name: string; 
  avatar: string; 
  typing: boolean; 
  lastSeen: number; 
  status?: DisplayPresenceStatus;
}

interface PresenceContextType { 
  online: PresenceEntry[]; 
  setTyping: (v: boolean) => void;
  /** Check if a user is online (from workspace presence) */
  isOnline: (userId: string) => boolean;
  /** Get last seen date for a user */
  getLastSeen: (userId: string) => Date | null;
  /** Get presence status for a user */
  getStatus: (userId: string) => DisplayPresenceStatus;
  /** Current user's effective presence. */
  myStatus: PresenceStatus;
  /** Current manual presence override; null means automatic. */
  manualStatus: ManualPresenceStatus;
  /** Publish a manual presence override. */
  setManualStatus: (status: ManualPresenceStatus) => void;
  /** Randomize demo presence (demo mode only) */
  randomizeDemoPresence: () => void;
}

// ─── Demo Status Generation ─────────────────────────────────────────────────
// Generate deterministic status based on user ID hash
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

type DemoStatus = DisplayPresenceStatus;

function getDemoStatus(userId: string, seed: number = 0): DemoStatus {
  const hash = hashString(userId + seed.toString());
  const mod = hash % 10;
  
  // Distribution: 40% online, 30% away, 20% offline, 10% busy
  if (mod < 4) return 'online';
  if (mod < 7) return 'away';
  if (mod < 9) return 'offline';
  return 'busy';
}

function getDemoLastSeen(status: DemoStatus, userId: string): number {
  const now = Date.now();
  const hash = hashString(userId);
  
  switch (status) {
    case 'online':
    case 'busy':
    case 'dnd':
      return now;
    case 'away':
      // 10-60 minutes ago
      return now - (10 + (hash % 50)) * 60 * 1000;
    case 'offline':
      // 2-48 hours ago
      return now - (2 + (hash % 46)) * 60 * 60 * 1000;
    default:
      return now;
  }
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

// Default workspace for presence tracking
// In production, this should come from user's workspace assignment
const DEFAULT_WORKSPACE_ID = 'default';

const PresenceProviderRoot: React.FC<{children: React.ReactNode}> = ({children}) => {
  // Safe attempt to access auth; if unavailable (e.g., preview/bare mode), continue with null user
  let user: { id: string; name?: string; avatar?: string; workspace_id?: string } | null = null;
  let authIsDemoMode = false;
  try {
    const auth = useAuth();
    user = auth.user;
    authIsDemoMode = auth.isDemoMode;
   
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_err) {
     
    console.warn('[PresenceProvider] Mounted without AuthProvider – running in degraded mode.');
  }
  
  // Use workspace presence for realtime online tracking
  const workspaceId = (user as { workspace_id?: string } | null)?.workspace_id || DEFAULT_WORKSPACE_ID;
  const shouldUseRealPresence = !isDemoMode && !authIsDemoMode && !!user;
  
  const { 
    presenceByUserId, 
    isOnline: wsIsOnline, 
    getLastSeen: wsGetLastSeen,
    myStatus,
    manualStatus,
    setManualStatus,
  } = useWorkspacePresence(shouldUseRealPresence ? workspaceId : null);
  
  const [demoOnline, setDemoOnline] = useState<PresenceEntry[]>([]);
  
  // Seed for randomizing demo presence (changed via randomizeDemoPresence)
  const [demoSeed, setDemoSeed] = useState(0);

  // Demo presence entries for all demo users with realistic mixed statuses
  // These IDs match the demo users in AuthContext and message senders in useChatMessages
  const demoParticipants: PresenceEntry[] = useMemo(() => {
    const demoUsers = createShowcaseUsers().map((account) => ({
      id: account.id,
      name: account.name,
      avatar: account.avatar ?? '',
    }));
    
    return demoUsers.map(u => {
      // Current user reflects the same automatic/manual status as production.
      const isCurrentUser = user?.id === u.id;
      const status: DemoStatus = isCurrentUser ? myStatus : getDemoStatus(u.id, demoSeed);
      const lastSeen = isCurrentUser ? Date.now() : getDemoLastSeen(status, u.id);
      
      return {
        ...u,
        typing: false,
        lastSeen,
        status,
      };
    });
  }, [user?.id, demoSeed, myStatus]);
  
  // Randomize demo presence (for testing/demo purposes)
  const randomizeDemoPresence = useCallback(() => {
    setDemoSeed(prev => prev + 1);
  }, []);

  // Mock: ping every 15s to update lastSeen (demo mode only)
  useEffect(() => { 
    if (!user || shouldUseRealPresence) return; 
    const id = user.id;
    
    const interval = setInterval(() => {
      setDemoOnline(prev => {
        const now = Date.now();
        const me: PresenceEntry = {
          id, 
          name: user.name || "Me", 
          avatar: user.avatar || "", 
          typing: false, 
          lastSeen: now,
          status: myStatus
        };
        const others = prev.filter(p => p.id !== id && now - p.lastSeen < 60_000);
        return [...others, me];
      });
    }, 15_000);
    
    // Initial update
    setDemoOnline(prev => {
      const now = Date.now();
      const me: PresenceEntry = {
        id, 
        name: user.name || "Me", 
        avatar: user.avatar || "", 
        typing: false, 
        lastSeen: now,
        status: myStatus
      };
      const others = prev.filter(p => p.id !== id);
      return [...others, me];
    });
    
     
     
    return () => clearInterval(interval);
   
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.name, user?.avatar, shouldUseRealPresence, myStatus]);

  // Convert workspace presence to PresenceEntry format
  const online = useMemo((): PresenceEntry[] => {
    if (!shouldUseRealPresence) {
      // In demo mode, combine current user presence with demo participants
      // Use a Map to deduplicate by id (current user takes precedence)
      const presenceMap = new Map<string, PresenceEntry>();
      
      // Add demo participants first
      demoParticipants.forEach(p => presenceMap.set(p.id, p));
      
      // Add current user and any other dynamic entries (overwrites if same id)
      demoOnline.forEach(p => presenceMap.set(p.id, p));
      
      return Array.from(presenceMap.values());
    }
    
    return Object.values(presenceByUserId)
      .map(p => ({
        id: p.user_id,
        name: p.display_name || 'Unknown',
        avatar: p.avatar_url || '',
        typing: false, // Typing is handled separately by useRoomTyping
        lastSeen: p.last_seen_at ? new Date(p.last_seen_at).getTime() : Date.now(),
        status: p.status,
      }));
  }, [shouldUseRealPresence, demoOnline, demoParticipants, presenceByUserId]);

  const setTyping = useCallback((v: boolean) => {
    if (!user) return;
    // In demo mode, update local state
    if (!shouldUseRealPresence) {
      setDemoOnline(p => p.map(o => o.id === user.id ? {...o, typing: v} : o));
    }
    // Note: Real typing is handled by useRoomTyping, not through presence
  }, [shouldUseRealPresence, user]);
  
  // isOnline helper - uses workspace presence when available
  const isOnline = useCallback((userId: string): boolean => {
    if (shouldUseRealPresence) {
      return wsIsOnline(userId);
    }
    // Demo mode fallback - check if user exists and is online/busy (not away/offline)
    const entry = online.find(p => p.id === userId);
    return entry?.status === 'online' || entry?.status === 'busy' || entry?.status === 'dnd';
  }, [shouldUseRealPresence, wsIsOnline, online]);
  
  // getLastSeen helper
  const getLastSeen = useCallback((userId: string): Date | null => {
    if (shouldUseRealPresence) {
      return wsGetLastSeen(userId);
    }
    // Demo mode fallback - check combined online array
    const entry = online.find(p => p.id === userId);
    return entry ? new Date(entry.lastSeen) : null;
  }, [shouldUseRealPresence, wsGetLastSeen, online]);
  
  // getStatus helper - get presence status for a user
  const getStatus = useCallback((userId: string): DisplayPresenceStatus => {
    if (shouldUseRealPresence) {
      const p = presenceByUserId[userId];
      if (!p) return 'offline';
      // Map workspace presence status
      return (p.status as DisplayPresenceStatus) || 'offline';
    }
    // Demo mode fallback
    const entry = online.find(p => p.id === userId);
    return entry?.status || 'offline';
  }, [shouldUseRealPresence, presenceByUserId, online]);

  const contextValue = useMemo<PresenceContextType>(() => ({
    online,
    setTyping,
    isOnline,
    getLastSeen,
    getStatus,
    myStatus,
    manualStatus,
    setManualStatus,
    randomizeDemoPresence,
  }), [getLastSeen, getStatus, isOnline, manualStatus, myStatus, online, randomizeDemoPresence, setManualStatus, setTyping]);

  return (
    <PresenceContext.Provider value={contextValue}>
      {children}
    </PresenceContext.Provider>
  );
};

/**
 * Provides the single workspace-presence subscription for the application.
 *
 * Chat wrappers can also be rendered in isolation (tests, previews, embedded
 * surfaces), so they keep their local PresenceProvider boundary. When the app
 * shell already provides presence, a nested boundary must reuse that context
 * instead of opening a second Supabase channel for the same workspace topic.
 */
export const PresenceProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const existingPresence = useContext(PresenceContext);
  return existingPresence
    ? <>{children}</>
    : <PresenceProviderRoot>{children}</PresenceProviderRoot>;
};

export const usePresence = () => {
  const c = useContext(PresenceContext);
  if (!c) throw new Error("PresenceProvider missing");
  return c;
};
